import type { TBaseResponse } from "@/models/common.model"

import type { TStartListRequest } from "@/models/start-list.model"
import type { HttpClient } from "@/services/http-common"
import type { ISO3Code } from "@/utils/lang/iso-map"
import { tryCatch } from "@/lib/try-catch"
import ApiServiceFactory from "@/services/api-service-factory"

export type TParticipantData = {
  participantId: string
  name: string
  surname: string
  delegation: ISO3Code | string & {}
  startingOrder: string
  startingSortOrder: number
  bib: string
  street: "left" | "right" | ""
  decorator: unknown
  personalInformation?: {
    surname: string
  }
}

export type TGroupData = {
  groupId: string
  name: string
  surname: string
  delegation: string
  startingOrder: string
  startingSortOrder: number
  participants: TParticipantData[]
  street: "left" | "right" | ""
}

export type TStartList = {
  id?: string
  code: string
  competitorType: string
  unitTypeCode?: string
  metadata: {
    discipline: string
    gender: string
    sportEvent: string
    category: string
    phase: string
    unit: string
    phaseCode: string
    unitCode: string
  }
  groups?: TGroupData[]
  participants: TParticipantData[]
  hasBye?: boolean
}

export type TStartListWinnersResponse = {
  hasPreviousPhase: boolean
  groups: number[]
  participants: string[]
}

export class StartlistService {
  private httpClient: HttpClient

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient
  }

  async fetchStartListByCode(unitCode: string): Promise<TStartList> {
    const response = await this.httpClient.get<TBaseResponse<TStartList>>(`/start-list/get-by-unit-code/${unitCode}`)

    const { value, message, isSuccess } = response

    if (isSuccess === false) {
      console.error("Error fetching start list by code:", message)
      throw new Error(`Failed to fetch start list for unit code ${unitCode}: ${message}`)
    }
    return value
  }

  async fetchStartList(): Promise<TStartList[]> {
    const { data, error } = await tryCatch(this.httpClient.get<TBaseResponse<TStartList[]>>("/start-list/get-all"))
    console.log("Start list response:", data)
    if (error) {
      console.error("Error fetching start list:", error)
      throw new Error("Failed to fetch start list")
    }

    return data
  }

  async createStartList(startList: TStartListRequest): Promise<TStartList> {
    const {
      data,
      error,
    } = await tryCatch(this.httpClient.post<TBaseResponse<TStartList>>("/start-list/create", startList))
    if (error) {
      console.error("Error creating start list:", error)
      throw new Error("Failed to create start list")
    }

    return data
  }

  async updateStartList(startList: TStartListRequest): Promise<TStartList> {
    const {
      data,
      error,
    } = await tryCatch(this.httpClient.put<TBaseResponse<TStartList>>("/start-list/update", startList))
    if (error) {
      console.error("Error creating start list:", error)
      throw new Error("Failed to create start list")
    }

    return data
  }

  async fetchStartListByPhaseCode(phaseCode: string | null): Promise<TStartList[]> {
    if (!phaseCode) {
      throw new Error("Phase code is required")
    }

    const {
      data,
      error,
    } = await tryCatch(this.httpClient.get<TBaseResponse<TStartList[]>>(`/start-list/get-by-phase-code/${phaseCode}`))
    if (error) {
      console.error("Error fetching start list by phase code:", error)
      throw new Error(`Failed to fetch start list for phase code ${phaseCode}: ${error}`)
    }
    return data
  }

  async fetchStartListWinners(unitCode: string): Promise<TStartListWinnersResponse> {
    const {
      data,
      error,
    } = await tryCatch(this.httpClient.get<TBaseResponse<TStartListWinnersResponse>>(`/start-list/winners/${unitCode}`))
    if (error) {
      console.error("Error fetching start list winners:", error)
    }
    return data!
  }

  async fetchStartListByDisciplineCode(disciplineCode: string): Promise<TStartList[]> {
    const {
      data,
      error,
    } = await tryCatch(this.httpClient.get<TBaseResponse<TStartList[]>>(`/start-list/get-by-discipline-code/${disciplineCode}`))
    if (error) {
      console.error("Error fetching start list by discipline code:", error)
      throw new Error(`Failed to fetch start list for discipline code ${disciplineCode}: ${error.message}`)
    }
    return data
  }
}

export const startListService = ApiServiceFactory.createService(StartlistService, "startlist")

// export default VenuesService
